import { zodResolver } from '@hookform/resolvers/zod';
import { slugify, type Workspace } from '@noconflict/contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateWorkspace } from '../../../entities/workspace';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import { Field } from '../../../shared/ui/field';
import { Input } from '../../../shared/ui/input';
import { schema, type Values } from './model';

/**
 * New workspace in `org` (needs `workspaces.manage`; the caller decides whether to offer it). The address
 * under the name is a preview: the database allocates the slug and may add a suffix, so `onCreated` gets
 * the real row to navigate to.
 */
export function CreateWorkspaceDialog({
  org,
  open,
  onOpenChange,
  onCreated,
}: {
  org: { id: string; slug: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (workspace: Workspace) => void;
}) {
  const { t } = useTranslation('workspace');
  const create = useCreateWorkspace(org.id);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset, watch, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
  });
  const name = watch('name').trim();

  function close(next: boolean) {
    if (!next) {
      reset();
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            try {
              const workspace = await create.mutateAsync(values);
              close(false);
              onCreated(workspace);
            } catch (e) {
              setError(identityErrorMessage(e));
            }
          })}
        >
          <Field
            label={t('create.name')}
            error={formState.errors.name?.message}
            {...(name
              ? {
                  description: t('create.urlPreview', {
                    path: `/o/${org.slug}/w/${slugify(name, 'workspace')}`,
                  }),
                }
              : {})}
          >
            <Input id="workspace-name" autoComplete="off" {...register('name')} />
          </Field>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => close(false)}>
              {t('create.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting}>
              {t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
